import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import CelebrationModal from '@/components/CelebrationModal';
import ModuleList from '@/components/ModuleList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checkInStatus, setCheckInStatus] = useState({
    morning_complete: false,
    midday_complete: false,
    evening_complete: false
  });
  const [celebrationMessage, setCelebrationMessage] = useState(null);
  const [moduleCelebration, setModuleCelebration] = useState(null);
  const [quickStrategy, setQuickStrategy] = useState(null);
  const [moduleProgress, setModuleProgress] = useState({ completed: 0, total: 14 });

  useEffect(() => {
    fetchCheckInStatus();
    checkMilestones();
    fetchQuickStrategy();
    checkModuleCelebrations();
    fetchModuleProgress();
  }, [location.state?.checkInComplete, location.state?.moduleCompleted]);

  const fetchCheckInStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/check-ins/today`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCheckInStatus({
          morning_complete: data.morning_complete,
          midday_complete: data.midday_complete,
          evening_complete: data.evening_complete
        });
      }
    } catch (error) {
      console.error('Error fetching check-in status:', error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const isMiddayTime = () => {
    const hour = new Date().getHours();
    return hour >= 11 && hour < 17; // 11 AM to 5 PM
  };

  const isEveningTime = () => {
    const hour = new Date().getHours();
    return hour >= 17; // 5 PM or later
  };

  const checkMilestones = async () => {
    try {
      // Check if we should show celebration (only once per session)
      const celebrationShown = sessionStorage.getItem('celebration_shown');
      if (celebrationShown) return;

      // Fetch progress stats
      const statsResponse = await fetch(`${BACKEND_URL}/api/progress/stats`, {
        credentials: 'include'
      });

      if (statsResponse.ok) {
        const stats = await statsResponse.json();

        // Check 5 check-ins milestone
        if (stats.this_week.check_ins_count >= 5 && !sessionStorage.getItem('checkins_5_celebrated')) {
          setCelebrationMessage({
            emoji: '✨',
            title: '5 days of showing up!',
            message: 'This is how change happens.',
            key: 'checkins_5_celebrated'
          });
          sessionStorage.setItem('celebration_shown', 'true');
          return;
        }

        // Check SOS success milestone
        const sosResponse = await fetch(`${BACKEND_URL}/api/sos`, {
          credentials: 'include'
        });

        if (sosResponse.ok) {
          const sosUses = await sosResponse.json();
          const successfulSOS = sosUses.filter(s => s.outcome === 'better').length;
          
          if (successfulSOS >= 3 && !sessionStorage.getItem('sos_3_celebrated')) {
            setCelebrationMessage({
              emoji: '💪',
              title: 'You have practiced healthy coping 3 times!',
              message: 'These skills are becoming stronger.',
              key: 'sos_3_celebrated'
            });
            sessionStorage.setItem('celebration_shown', 'true');
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error checking milestones:', error);
    }
  };

  const checkModuleCelebrations = async () => {
    try {
      // Check if coming from module completion
      if (location.state?.moduleCompleted) {
        const { moduleId, moduleNumber } = location.state.moduleCompleted;
        
        // Check if all modules are completed
        const milestonesResponse = await fetch(`${BACKEND_URL}/api/modules/milestones/check`, {
          credentials: 'include'
        });
        
        if (milestonesResponse.ok) {
          const milestones = await milestonesResponse.json();
          
          // Check if this is the first time seeing all-complete celebration
          if (milestones.all_completed && !sessionStorage.getItem('all_modules_celebrated')) {
            setModuleCelebration({
              type: 'all_modules'
            });
            sessionStorage.setItem('all_modules_celebrated', 'true');
            return;
          }
          
          // Otherwise show individual module celebration
          const celebrationKey = `module_${moduleId}_celebrated`;
          if (!sessionStorage.getItem(celebrationKey)) {
            setModuleCelebration({
              type: 'module',
              moduleNumber: moduleNumber,
              moduleId: moduleId
            });
            sessionStorage.setItem(celebrationKey, 'true');
          }
        }
      }
    } catch (error) {
      console.error('Error checking module celebrations:', error);
    }
  };

  const closeCelebration = () => {
    if (celebrationMessage?.key) {
      sessionStorage.setItem(celebrationMessage.key, 'true');
    }
    setCelebrationMessage(null);
  };

  const fetchQuickStrategy = async () => {
    try {
      // Get SOS uses count
      const sosResponse = await fetch(`${BACKEND_URL}/api/sos`, {
        credentials: 'include'
      });

      if (sosResponse.ok) {
        const sosUses = await sosResponse.json();
        
        // If less than 3 uses, show general button
        if (sosUses.length < 3) {
          setQuickStrategy({ type: 'general', label: 'I need support now' });
          return;
        }

        // Get effectiveness data
        const effectivenessResponse = await fetch(`${BACKEND_URL}/api/sos/effectiveness`, {
          credentials: 'include'
        });

        if (effectivenessResponse.ok) {
          const effectiveness = await effectivenessResponse.json();
          
          if (effectiveness.top_strategies && effectiveness.top_strategies.length > 0) {
            // Get top strategy
            let topStrategy = effectiveness.top_strategies[0];
            
            // If tied on success rate, apply priority: breathing > grounding > physical > talking
            const priorityOrder = ['breathing', 'grounding', 'physical', 'talking', 'support'];
            const topRate = topStrategy.success_rate;
            const tiedStrategies = effectiveness.top_strategies.filter(s => s.success_rate === topRate);
            
            if (tiedStrategies.length > 1) {
              // Find highest priority
              for (const priority of priorityOrder) {
                const found = tiedStrategies.find(s => s.strategy === priority);
                if (found) {
                  topStrategy = found;
                  break;
                }
              }
            }

            // Map to need_type for SOS component
            const strategyMap = {
              'breathing': { type: 'stress_relief', label: 'Try Breathing Exercise' },
              'grounding': { type: 'grounding', label: 'Try Grounding Technique' },
              'physical': { type: 'distraction', label: 'Try Physical Activity' },
              'talking': { type: 'connection', label: 'Reach Out for Connection' },
              'support': { type: 'connection', label: 'Get Support' }
            };

            const mapped = strategyMap[topStrategy.strategy];
            if (mapped) {
              setQuickStrategy(mapped);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching quick strategy:', error);
      setQuickStrategy({ type: 'general', label: 'I need support now' });
    }
  };

  const handleQuickStrategy = () => {
    if (quickStrategy?.type === 'general') {
      navigate('/sos');
    } else {
      navigate('/sos', { state: { directStrategy: quickStrategy.type } });
    }
  };

  const fetchModuleProgress = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/modules/milestones/check`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setModuleProgress({
          completed: data.completed_count || 0,
          total: 12
        });
      }
    } catch (error) {
      console.error('Error fetching module progress:', error);
    }
  };

  return (
    <div className="min-h-screen therapy-bg pb-20">
      <Navigation />
      
      {/* Module Celebration Modal */}
      {moduleCelebration && (
        <CelebrationModal
          type={moduleCelebration.type}
          moduleNumber={moduleCelebration.moduleNumber}
          moduleTitle={moduleCelebration.moduleTitle}
          onClose={() => setModuleCelebration(null)}
        />
      )}
      
      {/* Celebration Modal */}
      {celebrationMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="celebration-modal">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center space-y-4 shadow-2xl animate-slide-up">
            <div className="text-6xl mb-4">{celebrationMessage.emoji}</div>
            <h2 className="text-2xl font-bold text-[#2F3A3D]">
              {celebrationMessage.title}
            </h2>
            <p className="text-base text-[#CBD5E0] leading-relaxed">
              {celebrationMessage.message}
            </p>
            <Button
              onClick={closeCelebration}
              className="button-primary w-full rounded-full px-8 py-5 h-auto text-lg font-medium mt-6"
              data-testid="close-celebration-btn"
            >
              Thank you
            </Button>
          </div>
        </div>
      )}

      <main className="max-w-md mx-auto px-4 py-6 space-y-6 animate-fade-in">
        {/* Quick Strategy Button - Only show when user has 3+ SOS uses */}
        {quickStrategy && quickStrategy.type !== 'general' && (
          <button
            onClick={handleQuickStrategy}
            className="w-full bg-white border-2 border-[#6F8C9B] rounded-2xl p-5 hover:bg-[#F4F6F5] transition-all active:scale-[0.98] shadow-sm"
            data-testid="quick-strategy-btn"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#6F8C9B] rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-xs text-[#CBD5E0] font-medium">Your most effective strategy:</p>
                  <p className="text-base font-semibold text-[#6F8C9B]">{quickStrategy.label}</p>
                </div>
              </div>
              <svg className="w-5 h-5 text-[#6F8C9B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        )}

        {/* Welcome Message */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-white" data-testid="dashboard-welcome">
            Welcome back{user?.name ? `, ${user.name}` : ''}!
          </h1>
          <p className="text-base text-[#CBD5E0] leading-relaxed">
            Your journey to growth continues today
          </p>
        </div>

        {/* Values Reminder Card */}
        {(user?.selected_values?.length > 0 || user?.intention_statement) && (
          <div className="bg-gradient-to-br from-[#2D3640] to-[#252B33] rounded-2xl border border-[#00B4D8]/30 p-6 space-y-4" data-testid="values-reminder-card">
            {user?.selected_values?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-[#CBD5E0] mb-2">Your Values:</p>
                <div className="flex flex-wrap gap-2">
                  {user.selected_values.map((value, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-[#00B4D8]/20 border border-[#00B4D8]/50 text-[#00B4D8] rounded-full text-sm font-semibold"
                    >
                      {value}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {user?.intention_statement && (
              <div>
                <p className="text-sm font-medium text-[#CBD5E0] mb-2">I am becoming:</p>
                <p className="text-base text-white italic leading-relaxed font-medium">
                  {user.intention_statement}
                </p>
              </div>
            )}
            <p className="text-xs text-[#06D6A0] pt-2 border-t border-[#4A5568]">
              Remember why you&apos;re doing this work
            </p>
          </div>
        )}

        {/* Daily Practice Card */}
        <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] shadow-lg p-6 space-y-4" data-testid="daily-practice-card">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-white">
              Daily Practice
            </h2>
            <p className="text-sm text-[#CBD5E0]">
              {checkInStatus.morning_complete ? 'Great work today!' : `${getGreeting()}! Start your day with intention.`}
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-3">
            {!checkInStatus.morning_complete ? (
              <Button
                onClick={() => navigate('/morning-checkin')}
                className="w-full rounded-full px-6 py-6 h-auto text-base font-bold justify-start bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#06D6A0] hover:to-[#00B4D8] text-white"
                data-testid="morning-checkin-btn"
              >
                <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Morning Check-In
              </Button>
            ) : (
              <div className="flex items-center gap-3 p-4 bg-[#E9EEEB] rounded-xl">
                <svg className="w-6 h-6 text-[#8FAF9A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#2F3A3D]">Morning check-in complete</p>
                  <p className="text-xs text-[#CBD5E0]">You&apos;ve set your intention for today</p>
                </div>
              </div>
            )}
            
            {isMiddayTime() && (
              !checkInStatus.midday_complete ? (
                <Button
                  onClick={() => navigate('/midday-nudge')}
                  className="w-full rounded-full px-6 py-6 h-auto text-base font-medium justify-start bg-gradient-to-r from-[#D4A574] to-[#8FAF9A] text-white hover:opacity-90"
                  data-testid="midday-nudge-btn"
                >
                  <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Midday Check
                </Button>
              ) : (
                <div className="flex items-center gap-3 p-4 bg-[#E9EEEB] rounded-xl">
                  <svg className="w-6 h-6 text-[#8FAF9A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#2F3A3D]">Midday check complete</p>
                    <p className="text-xs text-[#CBD5E0]">Keep up the good work</p>
                  </div>
                </div>
              )
            )}
            
            {!isMiddayTime() && !checkInStatus.midday_complete && (
              <Button
                disabled
                className="w-full rounded-full px-6 py-6 h-auto text-base font-medium justify-start opacity-60 bg-[#D4A574]/20 text-[#CBD5E0]"
                data-testid="midday-nudge-disabled-btn"
              >
                <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Midday Check (Available 11 AM - 5 PM)
              </Button>
            )}
            
            {isEveningTime() && (
              !checkInStatus.evening_complete ? (
                <Button
                  onClick={() => navigate('/evening-reflection')}
                  className="button-secondary w-full rounded-full px-6 py-6 h-auto text-base font-medium justify-start"
                  data-testid="evening-reflection-btn"
                >
                  <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                  Evening Reflection
                </Button>
              ) : (
                <div className="flex items-center gap-3 p-4 bg-[#E9EEEB] rounded-xl">
                  <svg className="w-6 h-6 text-[#8FAF9A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#2F3A3D]">Evening reflection complete</p>
                    <p className="text-xs text-[#CBD5E0]">Rest well tonight</p>
                  </div>
                </div>
              )
            )}
            
            {!isEveningTime() && (
              <Button
                disabled
                className="button-secondary w-full rounded-full px-6 py-6 h-auto text-base font-medium justify-start opacity-60"
                data-testid="evening-reflection-disabled-btn"
              >
                <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                Evening Reflection (Available after 5 PM)
              </Button>
            )}
          </div>
        </div>

        {/* Your Journey Card */}
        <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] shadow-lg p-6 space-y-4" data-testid="your-journey-card">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">
                Your Journey
              </h2>
              <span className="text-sm font-bold text-[#00B4D8] bg-[#00B4D8]/20 px-3 py-1 rounded-full">
                {moduleProgress.completed}/{moduleProgress.total}
              </span>
            </div>
            <p className="text-sm text-[#CBD5E0]">
              Continue building your skills
            </p>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="h-3 bg-[#3A424D] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] transition-all duration-500 rounded-full"
                style={{ width: `${(moduleProgress.completed / moduleProgress.total) * 100}%` }}
              />
            </div>
            <p className="text-xs text-[#CBD5E0] text-center">
              {moduleProgress.completed === 0 
                ? 'Start your first module today!'
                : moduleProgress.completed === moduleProgress.total
                ? 'All modules completed! Keep practicing.'
                : `${moduleProgress.total - moduleProgress.completed} modules remaining`
              }
            </p>
          </div>
          
          <ModuleList />
        </div>

        {/* Need Support Card */}
        <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] shadow-lg p-6 space-y-4" data-testid="need-support-card">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-white">
              Need Support?
            </h2>
            <p className="text-sm text-[#CBD5E0]">
              Immediate help when you need it
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/sos')}
            className="w-full rounded-full px-6 py-6 h-auto text-base font-bold bg-gradient-to-r from-[#EF476F] to-[#FF6B35] hover:from-[#FF6B35] hover:to-[#EF476F] text-white shadow-lg hover:shadow-[#EF476F]/30 transition-all"
            data-testid="sos-btn"
          >
            <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            SOS - Get Help Now
          </Button>
        </div>

        {/* Your Progress Card */}
        <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] shadow-lg p-6 space-y-4" data-testid="your-progress-card">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-white">
              Your Progress
            </h2>
            <p className="text-sm text-[#CBD5E0]">
              See how you&apos;re growing
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/progress')}
            variant="outline"
            className="w-full rounded-full px-6 py-5 h-auto text-base font-medium border-[#E9EEEB] hover:bg-[#F4F6F5] text-[#6F8C9B]"
            data-testid="view-progress-btn"
          >
            View Capacity Growth
          </Button>
        </div>
      </main>
    </div>
  );
}
